/**
 * Refuse a form that maps TWO controls to the same Open Dental LIST field.
 *
 * WHY THIS IS A SAVE GATE, NOT A HINT
 * ViewForm.vue's `odDataToSave` builder walks every entry of `formData.controls`
 * and, when a column already has a value, JOINS the next one with '\n':
 *
 *     odDataToSave[col] = odDataToSave[col] ? odDataToSave[col] + '\n' + value : value
 *
 * For Diseases / Allergies / Medications the per-control value is an ARRAY of
 * `{text, value}` chart entries. Joining two arrays with '\n' produces a STRING,
 * and forms-service only accepts an ARRAY for those three columns — anything else
 * is treated as an EMPTY list, which marks EVERY active problem, allergy and
 * medication on the patient's chart resolved/inactive on every single submission.
 * For Referrals the backend spots the string and silently skips the write-back.
 *
 * Neither failure errors at submit time, so the builder refuses the save instead
 * of storing a form that quietly wipes charts (the "never store-and-report-success"
 * rule — CLAUDE.md, "Settings that gate a send").
 *
 * The rule is about the MAPPING, not the control type: a legacy `opendentaldata`
 * DISEASES dropdown and a new DISEASES_CATEGORIZED control both saved to Diseases
 * collide exactly the same way. And because the submit path iterates ALL of
 * `formData.controls`, placement and visibility are irrelevant — hidden controls
 * count too.
 *
 * IMPORT-FREE ON PURPOSE: cypress/node-tests/forms-od-unique-mapping.test.js loads
 * THIS file's source, strips the export and evaluates it, so the tests always
 * exercise the shipped code instead of a mirror that can drift.
 */

/**
 * The Open Dental columns that may be written by at most ONE control per form.
 * Exact, case-sensitive: these are the keys the submit path builds `odDataToSave`
 * with. Order matters — form-level errors report the first duplicated column here.
 * @type {string[]}
 */
const UNIQUE_OD_MAPPINGS = ['Diseases', 'Allergies', 'Medications', 'Referrals'];

/**
 * Normalise a control's `teamioODColumn` to a trimmed string.
 *
 * @param {Object} control - A control (may be null/undefined/not an object)
 * @returns {string} The trimmed mapping, or '' when there is none.
 */
function normalizedMapping(control) {
    if (!control || typeof control !== 'object') {
        return '';
    }
    const column = control.teamioODColumn;
    if (column === null || column === undefined) {
        return '';
    }
    return String(column).trim();
}

/**
 * A human-readable name for a control, for use inside an error message.
 *
 * Labels are authored in a rich-text editor, so they routinely carry markup
 * (`<p>`, `<strong>`, `<br>`) and ragged whitespace. Both are stripped so the
 * toast reads as a sentence.
 *
 * @param {Object} control - A control (may be null/undefined/not an object)
 * @returns {string} The label without tags, else `control.name`, else 'Untitled field'.
 */
function controlDisplayName(control) {
    if (!control || typeof control !== 'object') {
        return 'Untitled field';
    }

    const label = control.label === null || control.label === undefined ? '' : String(control.label);
    const stripped = label.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (stripped !== '') {
        return stripped;
    }

    const name = control.name === null || control.name === undefined ? '' : String(control.name).trim();
    if (name !== '') {
        return name;
    }

    return 'Untitled field';
}

/**
 * Every restricted Open Dental column that more than one control on the form saves to.
 *
 * Tolerates anything: a missing/null/non-object `formData`, a missing/null/non-object
 * `controls` map, and junk control entries — all yield [].
 *
 * @param {Object} formData - The live form (`formData.controls` is a keyed map)
 * @returns {Array<{column: string, controls: Array<{uniqueId: *, label: string}>}>}
 *   One entry per duplicated column, in UNIQUE_OD_MAPPINGS order; the controls inside
 *   each entry are in `Object.keys(formData.controls)` order. Empty when there are none.
 */
function findDuplicateOdMappings(formData) {
    if (!formData || typeof formData !== 'object') {
        return [];
    }
    const controls = formData.controls;
    if (!controls || typeof controls !== 'object') {
        return [];
    }

    const byColumn = {};
    UNIQUE_OD_MAPPINGS.forEach((column) => {
        byColumn[column] = [];
    });

    Object.keys(controls).forEach((key) => {
        const control = controls[key];
        const mapping = normalizedMapping(control);
        if (UNIQUE_OD_MAPPINGS.indexOf(mapping) === -1) {
            return;
        }
        byColumn[mapping].push({
            uniqueId: control.uniqueId,
            label: controlDisplayName(control)
        });
    });

    return UNIQUE_OD_MAPPINGS
        .filter((column) => byColumn[column].length > 1)
        .map((column) => ({ column, controls: byColumn[column] }));
}

/**
 * The Control Configuration sidebar gate.
 *
 * NOTE the control passed here is the sidebar's DEEP CLONE of the control being
 * edited, carrying the mapping the author just picked; the copy still sitting in
 * `formData.controls` holds the OLD mapping. So the clone's own entry (matched by
 * `uniqueId`) is IGNORED and only OTHER controls are considered.
 *
 * @param {Object} control - The cloned control being configured
 * @param {Object} formData - The live form the control belongs to
 * @returns {string} '' when the save is fine, else the reason it is refused.
 */
function getOdUniqueMappingError(control, formData) {
    const mapping = normalizedMapping(control);
    if (UNIQUE_OD_MAPPINGS.indexOf(mapping) === -1) {
        return '';
    }

    if (!formData || typeof formData !== 'object') {
        return '';
    }
    const controls = formData.controls;
    if (!controls || typeof controls !== 'object') {
        return '';
    }

    const editedId = control.uniqueId;
    const conflict = Object.keys(controls)
        .map((key) => controls[key])
        .find((other) => other
            && typeof other === 'object'
            && other.uniqueId !== editedId
            && normalizedMapping(other) === mapping);

    if (!conflict) {
        return '';
    }

    return `Only one field on a form can save to ${mapping} in Open Dental. "${controlDisplayName(conflict)}" already saves there. Clear or change Save to Open Dental Field on one of them.`;
}

/**
 * The form-level gate, run by both the create and the edit screen before saving.
 *
 * Reports the FIRST duplicated column only (one toast at a time); fixing it and
 * saving again surfaces the next.
 *
 * @param {Object} formData - The live form (may be null/undefined)
 * @returns {string} '' when no column is duplicated, else the reason the save is refused.
 */
function getFormOdUniqueMappingError(formData) {
    const duplicates = findDuplicateOdMappings(formData);
    if (duplicates.length === 0) {
        return '';
    }

    const duplicate = duplicates[0];
    const labels = duplicate.controls.map((entry) => `"${entry.label}"`).join(', ');

    return `Cannot save form: more than one field saves to ${duplicate.column} in Open Dental (${labels}). Only one field per form can save to Diseases, Allergies, Medications or Referrals. Clear or change Save to Open Dental Field on all but one.`;
}

export {
    UNIQUE_OD_MAPPINGS,
    controlDisplayName,
    findDuplicateOdMappings,
    getOdUniqueMappingError,
    getFormOdUniqueMappingError
};
